// RC10 Phase 2B-2 · <LinkedContactPicker> — inline single-Contact
// picker for a specific field on a draft entity (Tour pickup/dropoff/
// itinerary contact; Quote/Contract billing_contact).
//
// Contract:
//   value: {id, first_name, last_name, primary_email, mobile} | null
//   onChange(contact | null)  — parent commits to backend field.
//   customerId  — required for search filter (excludes cross-tenant
//                 Contacts, ContactLinker convention).
//   label       — field label displayed above the picker.
//   testidPrefix— data-testid namespace prefix for automated tests.
//
// Behavior:
//   • Empty state: search input + "Search" (debounced), result list.
//   • Selected state: identity block + "Change" + "Unlink" affordances.
//     Unlink CLEARS the local `value` (and by extension the backend
//     `contact_id`) without deleting the Contact record.
//   • Archived contacts are excluded from search results (backend
//     filter). Selecting an archived contact via legacy `value` shows
//     the identity WITH an "Archived" badge but not selectable in
//     the picker.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { contactApi } from "@/lib/entities";

const DEBOUNCE_MS = 220;

export function LinkedContactPicker({
  value,
  onChange,
  customerId,
  label = "Linked Contact",
  testidPrefix = "linked-contact-picker",
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(!value);
  const inputRef = useRef(null);

  const contextParams = useMemo(
    () => (customerId ? { linked_to_customer_id: customerId } : {}),
    [customerId],
  );

  useEffect(() => {
    let cancelled = false;
    if (!open || q.trim().length === 0) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const d = await contactApi.search({
          q,
          page_size: 15,
          ...contextParams,
        });
        if (!cancelled) setResults((d.results || []).filter((r) => !r.is_archived));
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, open, contextParams]);

  if (value && !open) {
    return (
      <div
        className="border border-coach-blue/40 bg-coach-blue/5 rounded-sm p-2 flex items-start justify-between gap-2"
        data-testid={`${testidPrefix}-selected`}
      >
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-widest text-coach-blue mb-0.5">
            {label}
          </div>
          <div className="text-[13px] font-medium text-carbon truncate flex items-center gap-2">
            {[value.preferred_name || value.first_name, value.last_name]
              .filter(Boolean)
              .join(" ") || "(no name)"}
            {value.is_archived && (
              <span
                className="text-[10px] uppercase tracking-widest text-amber-hold border border-amber-hold/40 rounded-sm px-1.5 py-0.5"
                data-testid={`${testidPrefix}-archived-badge`}
                aria-label="Archived contact"
              >
                Archived
              </span>
            )}
          </div>
          <div className="text-[11.5px] text-steel truncate">
            {[value.primary_email, value.mobile].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => {
              setOpen(true);
              setTimeout(() => inputRef.current?.focus(), 30);
            }}
            className="text-[11.5px] text-coach-blue hover:underline focus-visible:outline-2 focus-visible:outline-coach-blue"
            data-testid={`${testidPrefix}-change`}
          >
            Change
          </button>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-[11.5px] text-critical-red hover:underline focus-visible:outline-2 focus-visible:outline-critical-red inline-flex items-center gap-1"
            aria-label="Unlink Contact"
            data-testid={`${testidPrefix}-unlink`}
          >
            <X size={11} /> Unlink Contact
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-coach-silver rounded-sm p-2 space-y-2" data-testid={`${testidPrefix}-open`}>
      <div className="text-[10.5px] uppercase tracking-widest text-steel">{label}</div>
      <div className="relative">
        <Search
          size={13}
          className="absolute left-2 top-1/2 -translate-y-1/2 text-steel"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search linked Contacts by name, email, phone…"
          data-testid={`${testidPrefix}-search`}
          className="w-full border border-coach-silver rounded-sm pl-7 pr-8 py-1.5 text-[13px] bg-white focus-visible:outline-2 focus-visible:outline-coach-blue"
        />
        {loading && (
          <Loader2
            size={13}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-steel animate-spin"
            aria-hidden="true"
          />
        )}
      </div>
      {value && (
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[11.5px] text-steel hover:underline"
          data-testid={`${testidPrefix}-cancel-change`}
        >
          Cancel change
        </button>
      )}
      {q.trim().length > 0 && (
        <ul
          className="max-h-52 overflow-y-auto border border-coach-silver rounded-sm divide-y divide-coach-silver/60"
          data-testid={`${testidPrefix}-results`}
        >
          {!loading && results.length === 0 && (
            <li className="px-3 py-3 text-[12.5px] text-steel">
              No linked Contacts match "{q}". Link the Contact to this Client first.
            </li>
          )}
          {results.map((r) => (
            <li
              key={r.id}
              className="px-3 py-2 hover:bg-road-white cursor-pointer flex items-center justify-between"
              onClick={() => {
                onChange(r);
                setOpen(false);
                setQ("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onChange(r);
                  setOpen(false);
                  setQ("");
                }
              }}
              tabIndex={0}
              role="button"
              data-testid={`${testidPrefix}-result-${r.id}`}
            >
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-carbon truncate">
                  {[r.preferred_name || r.first_name, r.last_name].filter(Boolean).join(" ") || "(no name)"}
                </div>
                <div className="text-[11.5px] text-steel truncate">
                  {[r.primary_email, r.mobile].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              <span className="text-[11.5px] text-coach-blue shrink-0">Select →</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default LinkedContactPicker;
